import { faArrowUpRightFromSquare, faTruck } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Avatar, AvatarFallback, AvatarImage } from '@radix-ui/react-avatar';
import { Button } from '@shared/components/ui/button';
import type { ModalCenterRef } from '@shared/components/ui/ModalCenter';
import { SectionBox } from '@shared/components/ui/SectionBox';
import { FC, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useOrderTracking } from '../../hooks/useOrderTracking';
import type { OrderShipment } from '../../models/Order';
import { TrackingModal } from '../modals/TrackingModal';

interface TrackingProps {
  shipment?: OrderShipment;
}

// Muestra información de tracking y abre un modal con detalles
export const Tracking: FC<TrackingProps> = ({ shipment }) => {
  const { t } = useTranslation('viewOrdersModule');
  const modalRef = useRef<ModalCenterRef>(null);
  const { data: tracking, isLoading, error, lastEvent, getEventIcon } = useOrderTracking();

  const handleOpenModal = () => {
    modalRef.current?.openModal();
  };

  const courierName = shipment?.courier?.name || 'N/A';
  const trackingNumber = shipment?.tracking_number || 'N/A';

  const lastEventDescription = lastEvent?.description || '-';
  const lastEventDate = lastEvent?.date || t('sections.tracking.no-update');
  const lastEventIcon = lastEvent ? getEventIcon(lastEvent.code) : null;

  return (
    <>
      <SectionBox className="bg-card-bg">
        <div className="flex justify-between items-center">
          <h2 className="text-lg font-bold">{t('sections.tracking.title')}</h2>
          <Button variant="outline" size="sm" onClick={handleOpenModal} className="bg-card-bg">
            <FontAwesomeIcon icon={faArrowUpRightFromSquare} className="!w-3 !h-3" />
          </Button>
        </div>
        <div className="pt-6">
          <div className="relative flex flex-col gap-8">
            <div className="absolute left-5 top-0 h-full w-px bg-lines" />

            <div className="relative flex items-center gap-4">
              <Avatar className="z-10 h-10 w-10 rounded-full bg-card-border flex items-center justify-center">
                <AvatarImage src={shipment?.courier?.image_hash} alt={courierName} />
                <AvatarFallback className="text-text">{courierName.substring(0, 2).toUpperCase()}</AvatarFallback>
              </Avatar>
              <div className="flex flex-col">
                <span className="text-sm">{courierName}</span>
                <span className="text-xs text-complementary-text">{trackingNumber}</span>
              </div>
            </div>

            <div className="relative flex items-center gap-4">
              <div className="z-10 flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-hover-press">
                {isLoading ? (
                  <FontAwesomeIcon icon={faTruck} className="text-icons !w-4 !h-4" />
                ) : lastEventIcon ? (
                  <FontAwesomeIcon icon={lastEventIcon} className="text-icons !w-4 !h-4" />
                ) : (
                  <FontAwesomeIcon icon={faTruck} className="text-icons !w-4 !h-4" />
                )}
              </div>
              <div className="flex flex-col">
                {isLoading ? (
                  <span className="text-sm text-complementary-text">{t('sections.tracking.loading')}</span>
                ) : error ? (
                  <span className="text-sm text-subtext">{t('sections.tracking.error')}</span>
                ) : tracking && (tracking.events?.length || 0) > 0 ? (
                  <>
                    <span className="text-sm">{lastEventDescription}</span>
                    <span className="text-xs text-complementary-text">{lastEventDate}</span>
                  </>
                ) : (
                  <span className="text-sm text-subtext">{t('sections.tracking.empty')}</span>
                )}
              </div>
            </div>
          </div>
        </div>
      </SectionBox>
      <TrackingModal ref={modalRef} shipment={shipment} />
    </>
  );
};
